import { normalizeUpdateAction } from '@banking/ui/lib/workflow';
import { State, Action, advanceScreenAction, controllingIndividualsScreenUpdateAction, toggleLoadingAction } from './index';
import { shouldShowControllingIndividualsScreen } from './state';
import { LegalEntitiesBusinessEntityType, NonIndividualLegalEntitiesBusinessEntityType } from '@banking/api/utils/business-types';
import { accountOwnerIndividual, managerIndividual, ownerIndividuals } from '@banking/api/models/onboarding/individual';
import * as CiWorkflow from '@banking/ui/workflows/onboarding-ci';
import { concat, from, of } from 'rxjs';
import { assertNonNullable } from '@banking/ui/utils/assert';
const wrapInLoading = work => concat(of(toggleLoadingAction(true)), work, of(toggleLoadingAction(false)));
function handleAdvanceScreen(state, context) {
  switch (state.screen) {
    case State.Screen.BusinessType:
      if (state.businessType === 'individual') {
        // If sole-prop, the account owner must not be beneficial owner or manager
        const individuals = state.individuals.map(individual => {
          if (individual.isAccountOwner) {
            individual.isSignificantOwner = false;
            individual.isManager = false;
          }
          return individual;
        });
        return [{
          ...state,
          individuals,
          screen: State.Screen.None,
          done: true
        }, null];
      } else {
        return [{
          ...state,
          screen: state.reviewBusinessAddress ? State.Screen.BusinessAddress : State.Screen.EntityType
        }, null];
      }
    case State.Screen.BusinessAddress:
      return [{
        ...state,
        screen: State.Screen.EntityType
      }, null];
    case State.Screen.EntityType:
      if (context.kybEnabled) {
        return [{
          ...state,
          screen: State.Screen.BusinessManagement
        }, null];
      } else {
        return [{
          ...state,
          screen: State.Screen.None,
          done: true
        }, null];
      }
    case State.Screen.BusinessManagement:
      {
        // Updates the account owner Individual based on BusinessManagement selections
        const individuals = state.individuals.map(individual => {
          if (individual.isAccountOwner) {
            individual.isSignificantOwner = assertNonNullable(state.ownershipBps) >= 25_00;
            individual.isManager = state.isManager ?? false;
          }
          return individual;
        });
        if (shouldShowControllingIndividualsScreen(state)) {
          return [{
            ...state,
            individuals,
            screen: State.Screen.ControllingIndividuals
          }, () => CiWorkflow.initializeAction({
            businessName: state.businessName,
            requireManager: !state.isManager,
            hasSignificantOwners: Boolean(state.hasSignificantOwners),
            existingIndividuals: individuals
          })];
        } else {
          return [{
            ...state,
            individuals,
            screen: State.Screen.None,
            done: true
          }, null];
        }
      }
    case State.Screen.ControllingIndividuals:
      return [{
        ...state,
        screen: State.Screen.None,
        done: true
      }, null];
    case State.Screen.None:
      throw new Error('cannot advance from none screen');
  }
}
function handleBackButtonClick(state) {
  let newScreen;
  switch (state.screen) {
    case State.Screen.BusinessType:
      newScreen = State.Screen.None;
      break;
    case State.Screen.BusinessAddress:
      newScreen = State.Screen.BusinessType;
      break;
    case State.Screen.EntityType:
      newScreen = state.reviewBusinessAddress ? State.Screen.BusinessAddress : State.Screen.BusinessType;
      break;
    case State.Screen.BusinessManagement:
      newScreen = State.Screen.EntityType;
      break;
    case State.Screen.ControllingIndividuals:
      newScreen = State.Screen.BusinessManagement;
      state = {
        ...state,
        individuals: state.ciState.individuals
      };
      break;
    case State.Screen.None:
      throw new Error('cannot back from none screen');
  }
  return [{
    ...state,
    screen: newScreen
  }, null];
}
function handleBrowserHistoryChange(state, action) {
  switch (action.routeName) {
    case 'signup.business-type':
      return [{
        ...state,
        done: false,
        screen: State.Screen.BusinessType
      }, null];
    case 'signup.entity-type':
      return [{
        ...state,
        done: false,
        screen: State.Screen.EntityType
      }, null];
    case 'signup.business-management':
      return [{
        ...state,
        done: false,
        screen: State.Screen.BusinessManagement
      }, null];
    case 'signup.controlling-individuals':
      return [{
        ...state,
        done: false,
        screen: State.Screen.ControllingIndividuals
      }, null];
    default:
      return [{
        ...state,
        screen: State.Screen.None
      }, null];
  }
}
function handleBusinessManagementScreenUpdate(state, action, context) {
  state = {
    ...state,
    isManager: action.data.isManager,
    hasSignificantOwners: action.data.hasSignificantOwners,
    ownershipBps: action.data.ownershipBps
  };
  const work = () => wrapInLoading(from(context.saveKybData({
    screen: State.Screen.BusinessManagement,
    data: action.data
  }).then(advanceScreenAction)));
  return [state, work];
}
function handleBusinessTypeScreenUpdate(state, action, context) {
  state = {
    ...state,
    businessType: action.data.businessType
  };
  if (state.businessType === 'individual') {
    state = {
      ...state,
      entityType: LegalEntitiesBusinessEntityType.SOLE_PROPRIETORSHIP
    };
  } else if (state.entityType && !Object.values(NonIndividualLegalEntitiesBusinessEntityType).includes(state.entityType)) {
    state = {
      ...state,
      entityType: undefined
    };
  }
  const work = state.businessType === 'individual' ? () => wrapInLoading(from(context.saveKybData({
    screen: State.Screen.BusinessType,
    data: action.data
  }).then(advanceScreenAction))) : advanceScreenAction;
  return [state, work];
}
function handleControllingIndividualsScreenUpdate(state, action) {
  state = {
    ...state,
    individuals: action.data.individuals
  };
  return [state, advanceScreenAction];
}
function handleUserChangeEntityTypeUpdate(state, action) {
  const {
    entityType
  } = action;
  return [{
    ...state,
    entityType
  }, null];
}
function handleEntityTypeScreenUpdate(state, action, context) {
  state = {
    ...state,
    entityType: action.data.entityType
  };
  if (action.data.taxId) {
    const taxId = {
      value: action.data.taxId,
      serialNumber: action.data.taxId.slice(-4)
    };
    state = {
      ...state,
      taxId
    };
  }
  const work = () => wrapInLoading(from(context.saveKybData({
    screen: State.Screen.EntityType,
    data: action.data
  }).then(advanceScreenAction)));
  return [state, work];
}
function handleInitialize(state, action) {
  const {
    entityType,
    taxIdSerialNumber,
    existingIndividuals,
    businessName,
    reviewBusinessAddress
  } = action;
  state = {
    ...state,
    screen: State.Screen.BusinessType,
    businessName,
    individuals: existingIndividuals,
    entityType,
    taxId: {
      serialNumber: taxIdSerialNumber
    },
    reviewBusinessAddress
  };

  // If an entity type is set we can assume the businessType value.
  if (entityType) {
    if (entityType === LegalEntitiesBusinessEntityType.SOLE_PROPRIETORSHIP || entityType === LegalEntitiesBusinessEntityType.STRUCTURE_VALUE_NOT_SET) {
      state = {
        ...state,
        businessType: 'individual'
      };
    } else {
      state = {
        ...state,
        businessType: 'business'
      };
    }
  }

  // If there are existing individuals we can assume some other state that
  // we don't necessarily store on the server.
  if (existingIndividuals.length > 0) {
    if (managerIndividual(existingIndividuals)) {
      // A manager individual exists so the applicant is not a manager.
      state = {
        ...state,
        isManager: false
      };
    }
    if (accountOwnerIndividual(existingIndividuals)?.isManager) {
      state = {
        ...state,
        isManager: true
      };
    }
    if (ownerIndividuals(existingIndividuals).length > 0) {
      // One or more owner individuals exist so we can calculate a ownership%
      // for the applicannt to prefill.
      const ownershipBps = 10_000 - ownerIndividuals(existingIndividuals).length * 2500;
      state = {
        ...state,
        ownershipBps,
        hasSignificantOwners: true
      };
    }
  }
  return [state, null];
}
function handleReturnToFlowAction(state, context) {
  let screen;
  if (state.businessType === 'individual') {
    screen = State.Screen.BusinessType;
  } else if (!context.kybEnabled) {
    screen = State.Screen.EntityType;
  } else {
    if (shouldShowControllingIndividualsScreen(state)) {
      screen = State.Screen.ControllingIndividuals;
    } else {
      screen = State.Screen.BusinessManagement;
    }
  }
  return [{
    ...state,
    screen,
    done: false,
    ciState: {
      ...state.ciState,
      done: false
    }
  }, null];
}
function handleCiWorkflowUpdate(state, action, context) {
  const [newState, nextAction] = CiWorkflow.update(context)(state.ciState, action);
  state = {
    ...state,
    ciState: newState
  };
  if (state.ciState.done) {
    const doneAction = of(controllingIndividualsScreenUpdateAction({
      individuals: state.ciState.individuals
    }));
    return [state, () => concat(normalizeUpdateAction(nextAction?.() ?? null), doneAction)];
  }
  return [state, nextAction];
}
function handleToggleLoadingAction(state, action) {
  return [{
    ...state,
    loading: action.value
  }, null];
}

/* eslint-disable complexity */
export const update = context => (state, action) => {
  switch (action.type) {
    case CiWorkflow.Action.Type.AddManagerButtonClick:
    case CiWorkflow.Action.Type.AddOwnerButtonClick:
    case CiWorkflow.Action.Type.CloseDialog:
    case CiWorkflow.Action.Type.ContinueButtonClick:
    case CiWorkflow.Action.Type.DeleteIndividualSubmitRequest:
    case CiWorkflow.Action.Type.EditIndividualClick:
    case CiWorkflow.Action.Type.IndividualSubmitRequest:
    case CiWorkflow.Action.Type.CiInitialize:
    case CiWorkflow.Action.Type.OpenDialog:
    case CiWorkflow.Action.Type.UpdateIndividualsResponse:
    case CiWorkflow.Action.Type.UpdateIndividualsError:
      return handleCiWorkflowUpdate(state, action, context);
    case Action.Type.AdvanceScreen:
      return handleAdvanceScreen(state, context);
    case Action.Type.BackButtonClick:
      return handleBackButtonClick(state);
    case Action.Type.BrowserHistoryChange:
      return handleBrowserHistoryChange(state, action);
    case Action.Type.BusinessManagementScreenUpdate:
      return handleBusinessManagementScreenUpdate(state, action, context);
    case Action.Type.BusinessTypeScreenUpdate:
      return handleBusinessTypeScreenUpdate(state, action, context);
    case Action.Type.ControllingIndividualsScreenUpdate:
      return handleControllingIndividualsScreenUpdate(state, action);
    case Action.Type.UserChangeEntityType:
      return handleUserChangeEntityTypeUpdate(state, action);
    case Action.Type.EntityTypeScreenUpdate:
      return handleEntityTypeScreenUpdate(state, action, context);
    case Action.Type.Initialize:
      return handleInitialize(state, action);
    case Action.Type.ReturnToFlow:
      return handleReturnToFlowAction(state, context);
    case Action.Type.ToggleLoading:
      return handleToggleLoadingAction(state, action);
  }
};
/* eslint-enable complexity */